import { Box, Button, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import {
  CollabType,
  DEFAULT_CREATOR_UGC_MODIFICATION_REQUESTS,
  compensationOfferedProduct,
  type BriefTemplateContent,
  type CollabDeliveryMode,
} from '@fluenceur/types';
import {
  Calendar,
  EditPencil,
  Lock,
  MediaImageList,
  Page,
  PrivacyPolicy,
  Wallet,
} from 'iconoir-react';
import { useState, type ComponentType, type SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { isIdentityLocked } from '@/components/plan-locks/creator-locks';
import { useAuth } from '@/contexts/auth-context';
import type { ProfileUnlockTarget } from '@/hooks/actions/use-unlock-creator-profile-action';
import { useInvitationActions, useInvitationData } from '@/contexts/invitation-context';
import { useContactSummaryQuery } from '@/hooks/queries/use-contact-summary-query';
import { useEmailIntegrationStatus } from '@/hooks/use-email-integration-status';
import {
  briefCompensationLine,
  briefDeliverablesLine,
} from '@/pages/collabs/brief-templates/_shared/brief-summary-lines';
import { isMonoSend, isReachable } from '../outreach/collab-recipient-helpers';
import {
  bestAvailableChannel,
  CollabChannelPicker,
  isChannelSelectable,
  type CollabChannelCapabilities,
} from './collab-channel-picker';
import {
  CollabLegalIdentityBanner,
  useBrandLegalIdentityIncomplete,
} from './collab-legal-identity-banner';
import { CollabPaidBriefBlockers } from './CollabPaidBriefBlockers';
import { CollabGiftingAllowance, useCollabGiftingAllowance } from './collab-gifting-allowance';
import { briefTypeOfWizard } from '../collab-types';
import { collabPaidBriefViolations } from './collab-paid-brief-rules';
import { useCollabRecipients } from './use-collab-recipients';
import { useUgcRecipientTerms } from './use-ugc-recipient-terms';
import { recipientNameFor } from './collab-wizard-helpers';
import { ugcRightsParts } from '@/pages/collabs/brief-templates/_shared/ugc-rights-summary';

/** The masked creator of a mono send, by dataset reference when it has one, else by handle. */
const profileUnlockTargetOf = (creator: unknown): ProfileUnlockTarget | null => {
  const { sourceCreatorId, username } = (creator ?? {}) as {
    sourceCreatorId?: string | null;
    username?: string | null;
  };
  if (sourceCreatorId) return { sourceCreatorId };
  return username ? { username } : null;
};

interface CollabSendStepProps {
  onClose: () => void;
}

/** The recap's schedule line: a UGC counts from the product it ships, else from the funding. */
const ugcScheduleKey = (collabType: string | null, content: BriefTemplateContent): string => {
  if (collabType !== 'ugc') return 'scheduleLine';
  return compensationOfferedProduct(content.compensation, CollabType.UGC)
    ? 'ugcScheduleLine'
    : 'ugcScheduleLineFunding';
};

const SummaryLine = ({
  icon: Icon,
  children,
}: {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  children: React.ReactNode;
}) => (
  <HStack gap="2.5" align="flex-start">
    <Text color="fg.muted" display="inline-flex" flex="none" mt="0.5">
      <Icon width={14} height={14} />
    </Text>
    <Text fontSize="12.5px" color="primary.600" lineHeight="1.5">
      {children}
    </Text>
  </HStack>
);

/**
 * Channel step of the sponsored wizard: the "deal summary" card, the explicit
 * delivery-channel radios ("Remise de la proposition") and the immutability
 * note. Continue only commits the chosen mode and moves to the delivery step —
 * no network here; creation always happens from the delivery step's own CTA.
 */
const CollabSendStep = ({ onClose }: CollabSendStepProps) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const {
    collabContent,
    collabName,
    respondByDays,
    collabRecipientIdentityId,
    collabDeliveryMode,
    collabBriefApplicationId,
    collabType,
    creator,
  } = useInvitationData();
  const { setCollabDeliveryMode, setCurrentView } = useInvitationActions();
  const recipients = useCollabRecipients();
  const ugcTerms = useUgcRecipientTerms();

  // Strict send capability: only a healthy ("connected") mailbox lets email
  // leave the sender's address; a token that needs a reconnect would cascade to
  // a failure, so the email option treats it as not connected.
  const { hasUsableMailbox, isLoading: isIntegrationLoading } = useEmailIntegrationStatus();

  // Mono-only signal: an open Instagram conversation enables the DM channel.
  // Disabled for bulk (null identity), where the signal is not available.
  const { data: contactSummary } = useContactSummaryQuery(
    user?.currentBrandId,
    collabRecipientIdentityId ?? null
  );

  const legalIdentityIncomplete = useBrandLegalIdentityIncomplete();

  const isMono = isMonoSend(recipients);
  const monoMasked = isMono && isIdentityLocked(contactSummary);
  // An unlocked profile shows its address to the server before the store
  // holds it: the summary says it is known, and the send resolves it.
  const hasEmailRecipient =
    recipients.some((r) => isReachable(r) && !!r.email) ||
    (isMono && !monoMasked && !!contactSummary?.hasEmail);
  // The platform emails an active affiliate and a creator who applied on its
  // own, with no mailbox: the server substitutes nothing anywhere else.
  const emailByPlatform =
    isMono && (!!contactSummary?.affiliation?.active || !!collabBriefApplicationId);
  const emailLockedByPlan = recipients.some((r) => r.emailLocked) || monoMasked;
  const unlockTarget = monoMasked ? profileUnlockTargetOf(creator) : null;
  const capabilities: CollabChannelCapabilities = {
    emailAvailable: hasEmailRecipient || emailLockedByPlan,
    emailEnabled: hasEmailRecipient && (hasUsableMailbox || emailByPlatform),
    dmAvailable: isMono && !!contactSummary?.activeIgConversationId,
    // While the integration status resolves, hold the "not connected" copy and
    // the connect affordance back so the row never flashes the wrong state.
    emailStatusPending: isIntegrationLoading,
    emailNeedsMailbox:
      hasEmailRecipient && !hasUsableMailbox && !emailByPlatform && !isIntegrationLoading,
    emailByPlatform,
    emailLockedByPlan,
  };

  // Identity resolution still in flight on some lines: continuing now would
  // misclassify them as unreachable (definitive per-line failures at send).
  const isResolving = recipients.some((r) => r.channel === 'resolving');

  // Explicit user choice wins while it stays selectable (a return from the
  // compose step restores the stored mode); otherwise the best available
  // channel is preselected (email > dm > link).
  const [selected, setSelected] = useState<CollabDeliveryMode | null>(collabDeliveryMode);
  const mode: CollabDeliveryMode | null =
    selected && isChannelSelectable(selected, capabilities)
      ? selected
      : bestAvailableChannel(capabilities);

  const sendableCount = recipients.filter(isReachable).length;
  const gifting = useCollabGiftingAllowance(collabContent, sendableCount);

  if (!collabContent) return null;

  const isBulk = recipients.length > 1;
  // What the send will freeze for each recipient: the server's own answer, two
  // for a recipient it knows no account for. One count when they all agree.
  const grantedCounts = new Set(
    recipients.map(
      (recipient) =>
        (recipient.creatorIdentityId
          ? ugcTerms.get(recipient.creatorIdentityId)?.grantedModificationRequests
          : undefined) ?? DEFAULT_CREATOR_UGC_MODIFICATION_REQUESTS
    )
  );
  const sharedGrantedCount = grantedCounts.size === 1 ? [...grantedCounts][0] : null;
  const ugcRights =
    collabType === 'ugc' && collabContent.usageRights
      ? ugcRightsParts(collabContent, t, { withAdsShare: true, locale: i18n.language })
      : [];

  // A counterpart is what makes the brief a contract to be signed by someone
  // identifiable — a gifted product just as much as a payment. A brief that
  // promises nothing stays sendable. The entrance of the tunnel warns on the
  // bare condition; only here does it become a refusal.
  const needsLegalIdentity = collabContent.compensation.type !== 'none' && legalIdentityIncomplete;

  // A paid brief is an obligation to hold money: the composer states what makes
  // this one unsendable rather than letting the send route answer with a 422.
  const paidBriefViolations = collabPaidBriefViolations(
    collabContent,
    briefTypeOfWizard(collabType)
  );

  // Commit the chosen mode and move on. Creation NEVER happens here: every
  // mode sends from the delivery step's own CTA.
  const handleContinue = () => {
    if (!mode) return;
    setCollabDeliveryMode(mode);
    setCurrentView('collabDelivery');
  };

  return (
    <Flex direction="column" gap="3">
      <Box
        bg="background"
        borderWidth="1px"
        borderColor="cardBorder"
        rounded="lg"
        px="16px"
        py="3.5"
      >
        <Text
          fontFamily="heading"
          fontSize="10.5px"
          fontWeight="semibold"
          textTransform="uppercase"
          letterSpacing="0.06em"
          color="primary.500"
          mb="2.5"
        >
          {t('invitations.modal.collab.wizard.send.summaryTitle')}{' '}
          <Text
            as="span"
            fontFamily="body"
            fontSize="11px"
            fontWeight="normal"
            color="fg.muted"
            textTransform="none"
            letterSpacing="normal"
          >
            {isBulk
              ? t('invitations.modal.collab.wizard.send.summaryPinnedBulk')
              : t('invitations.modal.collab.wizard.send.summaryPinned', {
                  name: recipientNameFor(recipients[0], t),
                })}
          </Text>
        </Text>
        <VStack align="stretch" gap="2">
          <SummaryLine icon={MediaImageList}>{briefDeliverablesLine(collabContent, t)}</SummaryLine>
          <SummaryLine icon={Wallet}>
            {briefCompensationLine(collabContent, t, i18n.language)}
          </SummaryLine>
          {/* The server freezes each creator's own count at the send; this
              states the answer it gives for her now, from any entry point. */}
          {collabType === 'ugc' && (
            <SummaryLine icon={EditPencil}>
              {sharedGrantedCount === null
                ? t('invitations.modal.collab.wizard.send.modificationRequestsLineBulk')
                : t('invitations.modal.collab.wizard.send.modificationRequestsLine', {
                    requests: sharedGrantedCount,
                  })}
            </SummaryLine>
          )}
          {/* What the creator grants, as the rights clause states it. */}
          {collabType === 'ugc' && ugcRights.length > 0 && (
            <SummaryLine icon={PrivacyPolicy}>
              {t('invitations.modal.collab.wizard.send.rightsLine', {
                rights: ugcRights.join(' · '),
              })}
            </SummaryLine>
          )}
          <SummaryLine icon={Calendar}>
            {/* A UGC counts its deadlines from the product reception, or from the
                funding when it ships no product, never from the acceptance. */}
            {t(
              `invitations.modal.collab.wizard.send.${ugcScheduleKey(collabType, collabContent)}`,
              {
                resp: respondByDays,
                first: collabContent.timeline.firstContentDays,
                end: collabContent.timeline.endDays,
              }
            )}
          </SummaryLine>
          <SummaryLine icon={Page}>
            {t('invitations.modal.collab.wizard.send.briefLine', { name: collabName })}
          </SummaryLine>
          {/* One sentence, no dialog: this screen already carries the
              immutability note, and the clauses are read in full in the editor
              and on the tracking detail. A second reading surface here would
              be noise at the moment of an irreversible act. */}
          <SummaryLine icon={PrivacyPolicy}>
            {t('invitations.modal.collab.wizard.send.contractClausesLine')}
          </SummaryLine>
        </VStack>
      </Box>

      <Box>
        <Text
          fontFamily="heading"
          fontSize="10.5px"
          fontWeight="semibold"
          textTransform="uppercase"
          letterSpacing="0.06em"
          color="primary.500"
          mb="2"
        >
          {t('invitations.modal.collab.wizard.send.channelPicker.title')}
        </Text>
        <CollabChannelPicker
          value={mode}
          onChange={setSelected}
          capabilities={capabilities}
          unlockTarget={unlockTarget}
        />
      </Box>

      <HStack
        gap="2"
        color="fg.muted"
        align="flex-start"
        borderTopWidth="1px"
        borderColor="border.subtle"
        pt="2.5"
      >
        <Lock width={13} height={13} style={{ flexShrink: 0, marginTop: 2 }} />
        <Text fontSize="11.5px" lineHeight="1.5">
          {t('invitations.modal.collab.wizard.send.immutabilityNote')}
        </Text>
      </HStack>

      {needsLegalIdentity && <CollabLegalIdentityBanner variant="blocking" />}

      <CollabPaidBriefBlockers
        violations={paidBriefViolations}
        currency={collabContent.compensation.fixedPayment?.currency ?? 'EUR'}
        deliverables={collabContent.deliverables}
      />

      {gifting.quota && gifting.left !== null && (
        <CollabGiftingAllowance
          quota={gifting.quota}
          left={gifting.left}
          selected={sendableCount}
        />
      )}

      {isResolving && (
        <Text fontSize="11.5px" color="fg.muted" lineHeight="1.5">
          {t('invitations.modal.collab.wizard.send.resolvingRecipients')}
        </Text>
      )}

      <Flex justify="flex-end" gap="2" pt="1">
        <Button variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        {gifting.blocked ? (
          // The month's gifting invitations do not cover this send: the way
          // forward is the plan that lifts the limit, never a dead button.
          <Button colorPalette="primary" onClick={gifting.upgrade}>
            {t('invitations.modal.collab.wizard.send.giftingUpgrade')}
          </Button>
        ) : (
          <Button
            colorPalette="primary"
            onClick={handleContinue}
            loading={isResolving}
            disabled={
              !mode ||
              (sendableCount === 0 && !isResolving) ||
              needsLegalIdentity ||
              paidBriefViolations.length > 0
            }
          >
            {t('invitations.modal.collab.continue')}
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

export default CollabSendStep;
